import { FiCheck } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

import { IncorporationStageStatus, IncorporationTracking } from './types';

const circleClass: Record<IncorporationStageStatus, string> = {
  completed: 'bg-emerald-500 text-white',
  in_progress: 'bg-[#0C1FAA] text-white ring-4 ring-[#0C1FAA]/15',
  waiting_customer: 'bg-amber-500 text-white ring-4 ring-amber-100',
  not_started: 'border border-gray-300 bg-white text-gray-400'
};

interface Props {
  tracking: IncorporationTracking;
}

export const IncorporationTimeline = ({ tracking }: Props) => {
  const { t } = useTranslation();
  const currentIndex = tracking.stages.findIndex(
    (stage) => stage.status === 'in_progress' || stage.status === 'waiting_customer'
  );
  const currentStep = currentIndex >= 0 ? currentIndex + 1 : tracking.stages.length;

  return (
    <div>
      <p className="mb-5 text-sm font-medium text-gray-500">
        {t('incorporation.progress', { current: currentStep, total: tracking.stages.length })}
      </p>
      <ol className="grid grid-cols-1 gap-0 md:grid-cols-5">
        {tracking.stages.map((stage, index) => {
          const isLast = index === tracking.stages.length - 1;
          const done = stage.status === 'completed';
          return (
            <li key={stage.key} className="relative flex gap-4 md:flex-col md:items-center md:gap-3 md:px-2">
              {!isLast && (
                <span
                  className={`absolute left-4 top-9 h-[calc(100%-12px)] w-px md:left-1/2 md:top-4 md:h-0.5 md:w-full ${
                    done ? 'bg-emerald-400' : 'bg-gray-200'
                  }`}
                />
              )}
              <span
                className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${circleClass[stage.status]}`}
              >
                {done ? <FiCheck className="h-4 w-4" /> : index + 1}
              </span>
              <div className="pb-6 md:pb-0 md:text-center">
                <p className="text-sm font-semibold text-gray-900">{t(`incorporation.stages.${stage.key}.title`)}</p>
                <p className="mt-0.5 text-xs text-gray-500">{t(`incorporation.status.${stage.status}`)}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};
